/**
 * NeoCoreBICreditosPage — dashboard EXCLUSIVO de Crédito Emergente.
 *
 * Regla de negocio: nunca mezclar Emergente con Consumo. Fuente: RPC
 * bi_emergente() (filtra tipo emergente + es_prueba=false). Estética light-clean
 * con vida: lidera el dato de impacto (velocidad al desembolso), números que
 * cuentan hacia arriba y barras que crecen al montar. Revolvencia como volante
 * de crecimiento, cosechas al centro, demografía y eficiencia. Solo admin · GT.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowsClockwise, Lightning, ArrowRight } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { useRiesgoCartera, RiesgoCarteraPanel } from '@/components/RiesgoCartera'
import { formatQ } from '@/lib/format'

type RevPersona = { neo: string | null; nombre: string }
type Aging = { tramo: string; n: number; saldo: number }
type Cosecha = { mes: string; n: number; desembolsado: number; recuperado_pct: number; en_mora_n: number }
type Coloc = { mes: string; n: number; monto: number }
type Dim = { dim: string; n: number; monto?: number; desembolsado?: number; saldo?: number }
type BI = {
  generado_at: string; hoy: string
  rango: { desde: string | null; hasta: string; es_historia: boolean }
  ticket_promedio: number
  resumen: {
    colocado_total: number; creditos_total: number; asociados_total: number
    saldo_vigente: number; activos_n: number; liquidados_n: number
    recaudado_total: number; interes_ganado: number; mora_cobrada: number
  }
  revolvencia: { tasa_pct: number; liquidados_asoc: number; revolvieron_asoc: number; dias_represtamo_prom: number | null; por_liquidar_30: number; esperados_30: number; monto_esperado_30: number; revolvieron_lista: RevPersona[] | null }
  cartera: { saldo: number; creditos: number; al_dia_pct: number; par1_monto: number; par1_pct: number; en_mora_monto: number; mora_acumulada: number; aging: Aging[] }
  cosechas: Cosecha[]
  colocacion: Coloc[]
  demografia: { por_genero: Dim[]; por_departamento: Dim[]; por_actividad: Dim[] }
  por_tier: Dim[]; por_monto: Dim[]
  eficiencia: {
    velocidad_mins_mediana: number | null; velocidad_mins_p90: number | null
    pct_aprob_auto: number | null; aprob_pct: number | null
    funnel: { solicitadas: number; aprobadas: number; desembolsadas: number; rechazadas: number }
    pct_puntual: number | null; pct_anticipado: number | null; dias_prom_adelanto: number | null; fpd_pct: number | null
  }
}

const fmtInt = (n: number) => (n ?? 0).toLocaleString('es-GT')
const MES = ['', 'ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const mesLabel = (ym: string) => { const [y, m] = ym.split('-'); return `${MES[Number(m)]} ${y}` }
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s)
const generoLabel = (d: string) => (d === 'MASCULINO' ? 'Hombres' : d === 'FEMENINO' ? 'Mujeres' : cap(d))

const OK = 'var(--ok, #199E70)'
const BRAND = 'var(--brand-600, #FF5B04)'
const BLUE = '#2A78D6'
const PINK = '#E87BA4'

const ANIM_CSS = `
@keyframes biqFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.biq-sec{animation:biqFade .5s ease both}
.biq-card{transition:transform .18s ease,border-color .18s ease}
.biq-card:hover{transform:translateY(-2px);border-color:var(--n-300,#D3D1C7)}
`

/** Cuenta hacia arriba desde 0 al montar / cuando cambia el valor. */
function Count({ value, decimals = 0, sep = false, suffix = '', money = false }:
  { value: number; decimals?: number; sep?: boolean; suffix?: string; money?: boolean }) {
  const [v, setV] = useState(0)
  useEffect(() => {
    let raf = 0, t0 = 0
    const dur = 1000
    const tick = (ts: number) => {
      if (!t0) t0 = ts
      const p = Math.min(1, (ts - t0) / dur)
      const e = 1 - Math.pow(1 - p, 3)
      setV(value * e)
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value])
  const body = money ? formatQ(Math.round(v)) : sep ? Math.round(v).toLocaleString('es-GT') : v.toFixed(decimals)
  return <span>{body}{suffix}</span>
}

/** Barra que crece de 0 a pct al montar (transición CSS). */
function AnimBar({ pct, color, track = 'var(--n-100)', height = 12, radius = 6 }:
  { pct: number; color: string; track?: string; height?: number; radius?: number }) {
  const [w, setW] = useState(0)
  useEffect(() => { const r = requestAnimationFrame(() => setW(pct)); return () => cancelAnimationFrame(r) }, [pct])
  return (
    <div style={{ height, background: track, borderRadius: radius, overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(0, w)}%`, height: '100%', background: color, borderRadius: radius, transition: 'width 1.05s cubic-bezier(.22,1,.36,1)' }} />
    </div>
  )
}

export function NeoCoreBICreditosPage() {
  const [data, setData] = useState<BI | null>(null)
  const riesgo = useRiesgoCartera()  // mora = 31+ días calendario (0392)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modo, setModo] = useState<'historia' | 'mes'>('historia')
  const [verRev, setVerRev] = useState(false)
  const secDelay = useRef(0)

  const load = useCallback((m: 'historia' | 'mes') => {
    setLoading(true); setError(null)
    let desde: string | null = null
    let hasta: string | null = null
    if (m === 'mes') {
      const now = new Date()
      const y = now.getFullYear(), mo = now.getMonth()
      desde = `${y}-${String(mo + 1).padStart(2, '0')}-01`
      hasta = `${y}-${String(mo + 1).padStart(2, '0')}-${String(new Date(y, mo + 1, 0).getDate()).padStart(2, '0')}`
    }
    supabase.rpc('bi_emergente', { p_desde: desde, p_hasta: hasta }).then(({ data, error }) => {
      if (error) setError(error.message); else setData(data as BI)
      setLoading(false)
    })
  }, [])
  useEffect(() => { load(modo) }, [load, modo])

  if (loading && !data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--n-500)' }}>Cargando…</div>
  if (error) return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><div className="error-banner" style={{ margin: 0 }}>{error}</div><button className="btn" onClick={() => load(modo)}>Reintentar</button></div>
  if (!data) return null

  const { resumen: s, revolvencia: rv, cartera: c, eficiencia: ef } = data
  const gen = data.demografia.por_genero ?? []
  const genTotal = Math.max(1, gen.reduce((a, g) => a + g.n, 0))
  const deptos = (data.demografia.por_departamento ?? []).slice(0, 6)
  const deptoMax = Math.max(1, ...deptos.map((d) => d.n))
  const tierMax = Math.max(1, ...(data.por_tier ?? []).map((t) => t.n))
  const colocMax = Math.max(1, ...(data.colocacion ?? []).map((m) => m.monto))

  secDelay.current = 0
  const sec = (): React.CSSProperties => { const d = secDelay.current; secDelay.current += 0.06; return { animationDelay: `${d}s` } }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{ANIM_CSS}</style>

      {/* ===== HEADER ===== */}
      <div className="biq-sec" style={{ ...sec(), display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--n-900)' }}>Crédito Emergente</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: BRAND, background: 'var(--brand-50, #FEF0EA)', padding: '3px 9px', borderRadius: 20 }}>Producto exclusivo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'inline-flex', background: 'var(--n-100,#f1efe8)', borderRadius: 10, padding: 3 }}>
            {(['historia', 'mes'] as const).map((m) => (
              <button key={m} onClick={() => setModo(m)}
                style={{
                  border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, padding: '6px 14px', borderRadius: 8,
                  background: modo === m ? 'var(--surface,#fff)' : 'transparent',
                  color: modo === m ? 'var(--n-900)' : 'var(--n-500)',
                  boxShadow: modo === m ? '0 1px 2px rgba(0,0,0,.06)' : 'none',
                }}>
                {m === 'historia' ? 'Historia completa' : 'Mes actual'}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost" onClick={() => load(modo)}><ArrowsClockwise size={16} /> Actualizar</button>
        </div>
      </div>

      {/* ===== HERO · VELOCIDAD (lo que impacta) ===== */}
      <div className="biq-sec biq-card" style={{ ...sec(), background: 'var(--surface, #fff)', border: '0.5px solid var(--n-200)', borderRadius: 16, padding: '26px 24px', overflow: 'hidden' }}>
        <div style={{ fontSize: 12, color: 'var(--n-500)', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 6 }}>De la solicitud al desembolso</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 72, fontWeight: 800, lineHeight: 0.95, letterSpacing: -1, color: BRAND }}>
            {ef.velocidad_mins_mediana != null ? <><Count value={ef.velocidad_mins_mediana} /> <span style={{ fontSize: 32 }}>min</span></> : '—'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--n-500)', maxWidth: 300, lineHeight: 1.5 }}>mediana real. El dinero cae en la cuenta el mismo día, sin filas ni papeleo.</div>
        </div>
        <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', marginTop: 22, borderTop: '0.5px solid var(--n-200)', paddingTop: 18 }}>
          <HeroStat value={ef.pct_aprob_auto} suffix="%" label="aprobación automática" />
          <HeroStat value={riesgo.get('al_dia')?.pct ?? c.al_dia_pct} suffix="%" decimals={1} label="cartera al día" color={OK} />
          <HeroStat value={ef.pct_puntual} suffix="%" label="cuotas a tiempo" color={OK} />
          <HeroStat value={ef.pct_anticipado} suffix="%" label={ef.dias_prom_adelanto != null ? `pagan antes · ${ef.dias_prom_adelanto} d` : 'pagan antes'} />
        </div>
      </div>

      {/* ===== NÚMEROS ABSOLUTOS ===== */}
      <div className="biq-sec" style={{ ...sec(), fontSize: 11.5, color: 'var(--n-500)', marginTop: -6 }}>
        {modo === 'historia'
          ? 'Colocación y recaudo: todo el histórico · cartera y revolvencia siempre en vivo.'
          : `Colocación y recaudo: solo lo desembolsado este mes · cartera y revolvencia siempre en vivo.`}
      </div>
      <div className="biq-sec" style={{ ...sec(), ...grid(150) }}>
        <Tile label="Colocado total" valueNode={<><Count value={s.colocado_total} money /></>} sub={`ticket prom. ${formatQ(data.ticket_promedio)}`} />
        <Tile label="Créditos" valueNode={<Count value={s.creditos_total} />} sub={`${fmtInt(s.asociados_total)} asociados`} />
        <Tile label="Saldo vigente" valueNode={<Count value={s.saldo_vigente} money />} sub={`${fmtInt(s.activos_n)} activos`} />
        <Tile label="Recaudado" valueNode={<Count value={s.recaudado_total} money />} sub={`interés ${formatQ(s.interes_ganado)}`} />
      </div>

      {/* ===== REVOLVENCIA · VOLANTE ===== */}
      <div className="biq-sec biq-card" style={{ ...sec(), ...cardStyle }}>
        <Label icon={<ArrowsClockwise size={14} />}>Revolvencia · el volante de crecimiento</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
          <div style={{ minWidth: 130 }}>
            <div style={{ fontSize: 52, fontWeight: 800, lineHeight: 1, color: BRAND }}><Count value={rv.tasa_pct} decimals={1} suffix="%" /></div>
            <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 6, maxWidth: 170, lineHeight: 1.5 }}>
              de los que liquidan vuelven a pedir{rv.dias_represtamo_prom != null ? `, en ~${rv.dias_represtamo_prom} días` : ''}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--n-800)', minWidth: 30, textAlign: 'right' }}>{fmtInt(rv.liquidados_asoc)}</span>
              <div style={{ flex: 1 }}><AnimBar pct={100} color="var(--n-300, #D3D1C7)" height={24} /></div>
              <span style={{ fontSize: 12, color: 'var(--n-500)', minWidth: 88 }}>liquidaron</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: BRAND, minWidth: 30, textAlign: 'right' }}>{fmtInt(rv.revolvieron_asoc)}</span>
              <div style={{ flex: 1 }}><AnimBar pct={rv.tasa_pct} color={BRAND} height={24} /></div>
              <span style={{ fontSize: 12, color: 'var(--n-500)', minWidth: 88 }}>re-prestaron</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between', marginTop: 16, borderTop: '0.5px solid var(--n-200)', paddingTop: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--n-400)' }}>Próximos 30 días · vencen {fmtInt(rv.por_liquidar_30)}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--n-800)', marginTop: 2 }}>
              ~{fmtInt(rv.esperados_30)} re-préstamos esperados · {formatQ(rv.monto_esperado_30)}
            </div>
          </div>
          {(rv.revolvieron_lista?.length ?? 0) > 0 && (
            <button onClick={() => setVerRev((x) => !x)}
              style={{ border: '1px solid var(--n-200)', background: 'var(--surface,#fff)', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: 'var(--n-700)', cursor: 'pointer' }}>
              {verRev ? 'Ocultar quiénes' : `Ver los ${rv.revolvieron_lista!.length} que revolvieron`}
            </button>
          )}
        </div>
        {verRev && (rv.revolvieron_lista?.length ?? 0) > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {rv.revolvieron_lista!.map((p) => (
              <span key={(p.neo ?? '') + p.nombre} style={{ fontSize: 12, color: 'var(--n-700)', background: 'var(--n-100,#f1efe8)', borderRadius: 8, padding: '4px 10px' }}>
                {cap(p.nombre)} <span style={{ color: 'var(--n-400)' }}>· {p.neo ?? '—'}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ===== COSECHAS ===== */}
      <div className="biq-sec biq-card" style={{ ...sec(), ...cardStyle }}>
        <Label>Cosechas · recuperación por mes de desembolso</Label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {(data.cosechas ?? []).map((co) => (
            <div key={co.mes}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span style={{ color: 'var(--n-900)', fontWeight: 700 }}>{cap(mesLabel(co.mes))}</span>
                <span style={{ color: 'var(--n-500)' }}>{fmtInt(co.n)} créditos · {formatQ(co.desembolsado)}</span>
              </div>
              <AnimBar pct={co.recuperado_pct} color={OK} height={12} />
              <div style={{ fontSize: 11, color: 'var(--n-400)', marginTop: 4 }}>
                {co.recuperado_pct}% del capital recuperado{co.en_mora_n > 0 ? ` · ${co.en_mora_n} con atraso` : ' · cosecha joven'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ===== SALUD + COLOCACIÓN ===== */}
      <div className="biq-sec" style={{ ...sec(), ...grid(300) }}>
        <div className="biq-card" style={cardStyle}>
          <Label>Salud de la cartera</Label>
          <RiesgoCarteraPanel rows={riesgo.rows} />
        </div>

        <div className="biq-card" style={cardStyle}>
          <Label>Colocación mensual</Label>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, height: 130, padding: '0 8px' }}>
            {(data.colocacion ?? []).map((m) => (
              <div key={m.mes} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--n-800)' }}>{formatQ(m.monto)}</span>
                <VBar heightPct={Math.max(6, Math.round((m.monto / colocMax) * 100))} />
                <span style={{ fontSize: 11, color: 'var(--n-500)' }}>{cap(MES[Number(m.mes.split('-')[1])])} · {m.n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== DEMOGRAFÍA + NIVEL ===== */}
      <div className="biq-sec" style={{ ...sec(), ...grid(300) }}>
        <div className="biq-card" style={cardStyle}>
          <Label>Demografía · género</Label>
          <div style={{ display: 'flex', height: 34, borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
            {gen.map((g, idx) => (
              <div key={g.dim} title={`${generoLabel(g.dim)}: ${g.n}`} style={{ width: `${(g.n / genTotal) * 100}%`, background: idx === 0 ? BLUE : PINK, display: 'flex', alignItems: 'center', paddingLeft: 12, color: idx === 0 ? '#fff' : '#4B1528', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {generoLabel(g.dim)} {g.n}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--n-500)', lineHeight: 1.6 }}>
            {gen.map((g) => `${generoLabel(g.dim)} ${formatQ(g.monto ?? 0)}`).join(' · ')}.
            {deptos.length > 0 && <><br />Top departamentos:</>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 8 }}>
            {deptos.map((d) => (
              <div key={d.dim} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--n-700)', minWidth: 96, textTransform: 'capitalize' }}>{d.dim.toLowerCase()}</span>
                <div style={{ flex: 1 }}><AnimBar pct={(d.n / deptoMax) * 100} color="var(--n-300, #D3D1C7)" height={14} radius={4} /></div>
                <span style={{ fontSize: 12, color: 'var(--n-500)', minWidth: 24, textAlign: 'right' }}>{d.n}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="biq-card" style={cardStyle}>
          <Label>Por nivel Hunter</Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(data.por_tier ?? []).map((t) => (
              <div key={t.dim} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--n-800)', minWidth: 78, textTransform: 'capitalize' }}>{t.dim}</span>
                <div style={{ flex: 1 }}><AnimBar pct={(t.n / tierMax) * 100} color={t.dim === 'sin nivel' ? 'var(--n-300, #D3D1C7)' : OK} height={18} radius={5} /></div>
                <span style={{ fontSize: 12, color: 'var(--n-500)', minWidth: 92, textAlign: 'right' }}>{t.n} · {formatQ(t.desembolsado ?? 0)}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, borderTop: '0.5px solid var(--n-200)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(data.por_monto ?? []).map((m) => (
              <div key={m.dim} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'var(--n-600)' }}>{m.dim}</span>
                <span style={{ color: 'var(--n-800)', fontWeight: 600 }}>{m.n} · {formatQ(m.desembolsado ?? 0)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== EFICIENCIA · DETALLE ===== */}
      <div className="biq-sec biq-card" style={{ ...sec(), ...cardStyle }}>
        <Label icon={<Lightning size={14} weight="fill" />}>Eficiencia del proceso · detalle</Label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--n-500)' }}>Funnel:</span>
          <FunnelStep>{fmtInt(ef.funnel.solicitadas)} solicitadas</FunnelStep>
          <ArrowRight size={14} color="var(--n-400)" />
          <FunnelStep>{fmtInt(ef.funnel.aprobadas)} aprobadas</FunnelStep>
          <ArrowRight size={14} color="var(--n-400)" />
          <FunnelStep>{fmtInt(ef.funnel.desembolsadas)} desembolsadas</FunnelStep>
          <span style={{ fontSize: 12, color: 'var(--n-400)', marginLeft: 'auto' }}>
            FPD {ef.fpd_pct ?? 0}% · {ef.funnel.rechazadas === 0 ? 'sin rechazos' : `${ef.funnel.rechazadas} rechazo${ef.funnel.rechazadas > 1 ? 's' : ''}`}
            {ef.velocidad_mins_p90 != null ? ` · p90 ${ef.velocidad_mins_p90} min` : ''}
          </span>
        </div>
      </div>

      <p className="biq-sec" style={{ ...sec(), color: 'var(--n-400)', fontSize: 11, margin: 0 }}>
        Emergente limpio · {fmtInt(s.creditos_total)} créditos · {fmtInt(s.asociados_total)} asociados · {formatQ(s.colocado_total)} colocado ·
        {' '}actualizado {new Date(data.generado_at).toLocaleString('es-GT')} (GT {data.hoy}).
      </p>
    </div>
  )
}

const grid = (min: number): React.CSSProperties => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 12 })
const cardStyle: React.CSSProperties = { padding: 18, background: 'var(--surface, #fff)', border: '0.5px solid var(--n-200)', borderRadius: 12 }

function Label({ children, icon }: { children: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--n-400)', marginBottom: 14 }}>
      {icon}{children}
    </div>
  )
}

function HeroStat({ value, suffix, label, color, decimals = 0 }: { value: number | null; suffix?: string; label: string; color?: string; decimals?: number }) {
  return (
    <div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color ?? 'var(--n-900)' }}>
        {value != null ? <Count value={value} decimals={decimals} suffix={suffix} /> : '—'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--n-500)', marginTop: 2 }}>{label}</div>
    </div>
  )
}

function Tile({ label, valueNode, sub }: { label: string; valueNode: React.ReactNode; sub?: string }) {
  return (
    <div style={{ background: 'var(--n-50, #F7F6F2)', borderRadius: 10, padding: 14 }}>
      <div style={{ fontSize: 12, color: 'var(--n-500)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--n-900)', marginTop: 3 }}>{valueNode}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--n-400)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function VBar({ heightPct }: { heightPct: number }) {
  const [h, setH] = useState(0)
  useEffect(() => { const r = requestAnimationFrame(() => setH(heightPct)); return () => cancelAnimationFrame(r) }, [heightPct])
  return <div style={{ width: 52, height: `${h}%`, background: BLUE, borderRadius: '4px 4px 0 0', transition: 'height 1.05s cubic-bezier(.22,1,.36,1)' }} />
}

function FunnelStep({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--n-800)' }}>{children}</span>
}

